import React, { useEffect, useState, useMemo } from "react";
import { api } from "../lib/api";
import {
  Check, ChevronRight, X, Sparkles, Settings as SettingsIcon, Clock4, Users,
  Link2, Bell, Mail, CreditCard, ChevronDown, ChevronUp, ShieldCheck,
} from "lucide-react";

const DISMISS_KEY = "prenotoo_onboarding_dismissed";
const COLLAPSED_KEY = "prenotoo_onboarding_collapsed";

/**
 * OnboardingWizard — checklist contestuale che guida un nuovo titolare
 * dai primi passi al primo link prenotabile.
 *
 * Stato derivato dalle API esistenti (no nuove tabelle):
 *  1. Servizi (almeno 1)
 *  2. Orari (almeno 1 giorno aperto)
 *  3. Link pubblico copiato/condiviso (presunto se ha ≥1 booking)
 *  4. Notifiche push attivate sul device (opzionale ma fortemente consigliato)
 *  5. Email Resend configurata (opzionale)
 *  6. Pagamenti Stripe (opzionale)
 *
 * Si auto-collassa una volta completati i punti 1-3. Il pulsante "Più tardi"
 * la nasconde permanentemente (memorizzato in localStorage per business_id).
 */
export default function OnboardingWizard({ business, onJumpTab }) {
  const bid = business?.id;
  const [services, setServices] = useState([]);
  const [hours, setHours] = useState({});
  const [bookings, setBookings] = useState([]);
  const [emailCfg, setEmailCfg] = useState(null);
  const [stripeCfg, setStripeCfg] = useState(null);
  const [privacyCfg, setPrivacyCfg] = useState(null);
  const [pushReady, setPushReady] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    if (!bid) return;
    const dKey = `${DISMISS_KEY}_${bid}`;
    const cKey = `${COLLAPSED_KEY}_${bid}`;
    setDismissed(!!localStorage.getItem(dKey));
    setCollapsed(!!localStorage.getItem(cKey));

    Promise.all([
      api.get(`/businesses/${bid}`).catch(() => ({ data: { services: [] } })),
      api.get(`/businesses/${bid}/hours`).catch(() => ({ data: { hours: {} } })),
      api.get(`/bookings/business/${bid}`).catch(() => ({ data: [] })),
      api.get(`/businesses/${bid}/email`).catch(() => ({ data: null })),
      api.get(`/businesses/${bid}/stripe`).catch(() => ({ data: null })),
      api.get(`/businesses/${bid}/privacy-settings`).catch(() => ({ data: { completed: false } })),
    ]).then(([s, h, b, em, st, priv]) => {
      setServices(s.data?.services || []);
      setHours(h.data?.hours || {});
      setBookings(b.data || []);
      setEmailCfg(em.data);
      setStripeCfg(st.data);
      setPrivacyCfg(priv.data);
      setLoading(false);
    });

    // Best-effort: detect existing push subscription on this device
    if ("Notification" in window && navigator.serviceWorker) {
      navigator.serviceWorker.getRegistration().then((reg) => {
        if (!reg) return;
        reg.pushManager.getSubscription().then((sub) => {
          setPushReady(!!sub && Notification.permission === "granted");
        });
      });
    }
  }, [bid]);

  const steps = useMemo(() => {
    const hasServices = services.length > 0;
    const hasOpenDay = Object.values(hours || {}).some((h) => h?.active);
    const hasFirstBooking = bookings.length > 0;
    const hasEmail = !!emailCfg?.api_key_masked;
    const hasStripe = !!stripeCfg?.configured;
    const hasPrivacy = !!privacyCfg?.completed;

    return [
      {
        key: "privacy",
        title: "Pubblica l'informativa privacy clienti",
        desc: "Compila i dati della tua attività: generiamo un'informativa GDPR-compliant da linkare ai tuoi clienti.",
        cta: "Vai a Privacy",
        tab: "privacy",
        icon: ShieldCheck,
        done: hasPrivacy,
        required: true,
      },
      {
        key: "services",
        title: "Aggiungi i tuoi servizi",
        desc: "Almeno uno: nome, durata, prezzo. È quello che i clienti possono prenotare.",
        cta: "Vai a Servizi",
        tab: "services",
        icon: SettingsIcon,
        done: hasServices,
        required: true,
      },
      {
        key: "hours",
        title: "Definisci gli orari di apertura",
        desc: "Imposta i giorni e le fasce in cui accetti prenotazioni.",
        cta: "Vai a Orari",
        tab: "hours",
        icon: Clock4,
        done: hasOpenDay,
        required: true,
      },
      {
        key: "share",
        title: "Condividi il link pubblico",
        desc: "Copialo dalla banner in alto e mettilo su Instagram, Google, WhatsApp.",
        cta: "Vedi link",
        tab: "calendar",
        icon: Link2,
        done: hasFirstBooking, // first booking is the proxy for "shared"
        required: true,
      },
      {
        key: "push",
        title: "Attiva le notifiche su questo dispositivo",
        desc: "Ricevi un avviso istantaneo quando un cliente prenota, cancella o modifica.",
        cta: "Vai a Notifiche",
        tab: "notifications",
        icon: Bell,
        done: pushReady,
        required: false,
      },
      {
        key: "email",
        title: "Email automatiche (opzionale)",
        desc: "Configura Resend per inviare conferme e reminder via email. Le push funzionano già senza.",
        cta: "Vai a Email",
        tab: "email",
        icon: Mail,
        done: hasEmail,
        required: false,
      },
      {
        key: "stripe",
        title: "Accetta acconti online (opzionale)",
        desc: "Configura Stripe per richiedere un acconto al momento della prenotazione.",
        cta: "Vai a Pagamenti",
        tab: "stripe",
        icon: CreditCard,
        done: hasStripe,
        required: false,
      },
    ];
  }, [services, hours, bookings, emailCfg, stripeCfg, privacyCfg, pushReady]);

  const requiredDone = steps.filter((s) => s.required && s.done).length;
  const totalRequired = steps.filter((s) => s.required).length;
  const optionalDone = steps.filter((s) => !s.required && s.done).length;
  const totalOptional = steps.filter((s) => !s.required).length;
  const allRequiredDone = requiredDone === totalRequired;

  if (loading || dismissed) return null;
  // Hide automatically once everything (required + optional) is done
  if (allRequiredDone && optionalDone === totalOptional) return null;

  function close(permanent) {
    if (permanent) {
      localStorage.setItem(`${DISMISS_KEY}_${bid}`, String(Date.now()));
      setDismissed(true);
    } else {
      const cKey = `${COLLAPSED_KEY}_${bid}`;
      const next = !collapsed;
      if (next) localStorage.setItem(cKey, "1");
      else localStorage.removeItem(cKey);
      setCollapsed(next);
    }
  }

  const progress = Math.round((requiredDone / totalRequired) * 100);

  return (
    <div
      className="rounded-3xl border border-blue-200 bg-gradient-to-br from-blue-50 via-white to-white p-5 mb-6 shadow-sm dark:border-blue-900/50 dark:from-blue-950/40 dark:via-zinc-900 dark:to-zinc-900"
      data-testid="onboarding-wizard"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="inline-flex items-center gap-2 text-[11px] font-semibold tracking-[0.2em] uppercase text-blue-600 dark:text-blue-400">
            <Sparkles className="h-3 w-3" /> Onboarding
          </div>
          <h3 className="font-display text-xl md:text-2xl font-medium tracking-tight mt-1 text-zinc-900 dark:text-zinc-100">
            {allRequiredDone
              ? "Sei pronto! Completa i passi opzionali per il massimo"
              : `Imposta Prenotoo in ${totalRequired} passi`}
          </h3>
          <p className="text-xs text-zinc-600 mt-1 dark:text-zinc-400">
            {allRequiredDone
              ? `${optionalDone} di ${totalOptional} passi opzionali completati.`
              : `${requiredDone} di ${totalRequired} essenziali completati · ${progress}%`}
          </p>
          <div className="mt-3 h-1.5 w-full max-w-md rounded-full bg-blue-100 overflow-hidden dark:bg-blue-950/60">
            <div
              className="h-full bg-gradient-to-r from-blue-500 to-blue-600 transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => close(false)}
            className="h-8 w-8 rounded-full bg-white/80 hover:bg-white text-zinc-600 flex items-center justify-center dark:bg-zinc-800/70 dark:hover:bg-zinc-800 dark:text-zinc-300"
            title={collapsed ? "Espandi" : "Riduci"}
            data-testid="onboarding-collapse"
          >
            {collapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
          </button>
          <button
            onClick={() => close(true)}
            className="h-8 w-8 rounded-full bg-white/80 hover:bg-white text-zinc-600 flex items-center justify-center dark:bg-zinc-800/70 dark:hover:bg-zinc-800 dark:text-zinc-300"
            title="Nascondi definitivamente"
            data-testid="onboarding-dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {!collapsed && (
        <ul className="mt-5 space-y-1.5" data-testid="onboarding-steps">
          {steps.map((s, i) => (
            <li
              key={s.key}
              className={`group flex items-center gap-3 rounded-xl px-3 py-2.5 transition ${
                s.done ? "opacity-60" : "hover:bg-white/80 dark:hover:bg-zinc-800/60"
              }`}
              data-testid={`onboarding-step-${s.key}`}
              data-done={s.done ? "1" : "0"}
            >
              <div
                className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
                  s.done ? "bg-emerald-500 text-white" : s.required ? "bg-blue-100 text-blue-700 dark:bg-blue-950/60 dark:text-blue-300" : "bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-300"
                }`}
              >
                {s.done ? <Check className="h-4 w-4" /> : i + 1}
              </div>
              <s.icon className={`h-4 w-4 shrink-0 ${s.done ? "text-zinc-400 dark:text-zinc-500" : "text-zinc-700 dark:text-zinc-300"}`} />
              <div className="flex-1 min-w-0">
                <div className={`text-sm font-semibold ${s.done ? "line-through text-zinc-500 dark:text-zinc-500" : "text-zinc-900 dark:text-zinc-100"}`}>
                  {s.title}
                  {!s.required && <span className="ml-2 text-[10px] uppercase tracking-wider font-bold text-zinc-400 dark:text-zinc-500">Opzionale</span>}
                </div>
                {!s.done && <div className="text-xs text-zinc-500 mt-0.5 dark:text-zinc-400">{s.desc}</div>}
              </div>
              {!s.done && (
                <button
                  onClick={() => onJumpTab && onJumpTab(s.tab)}
                  className="h-8 px-3 rounded-full bg-zinc-900 hover:bg-blue-600 text-white text-xs font-semibold inline-flex items-center gap-1 transition shrink-0 dark:bg-zinc-100 dark:text-zinc-900 dark:hover:bg-blue-500 dark:hover:text-white"
                  data-testid={`onboarding-step-cta-${s.key}`}
                >
                  {s.cta} <ChevronRight className="h-3 w-3" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
